import { api } from "./client";

export type PropertyType = "OWNERSHIP" | "BARE_OWNERSHIP" | "OTHER";
export type MortgageCalculationMethod = "FRENCH";

export interface MortgageAmortization {
  id: string;
  mortgage_id: string;
  date: string;
  amount: number;
  reduce_term: boolean;
  created_at: string;
}

export interface Mortgage {
  id: string;
  property_id: string;
  calculation_method: MortgageCalculationMethod;
  start_date: string;
  installments: number;
  principal: number;
  interest_rate: number;
  amortizations: MortgageAmortization[];
  // Computed
  monthly_payment: number;
  total_cost: number;
  total_interest: number;
  outstanding_principal: number;
  paid_principal: number;
  installments_paid: number;
  installments_remaining: number;
  end_date: string;
}

export interface MortgageCreate {
  calculation_method: MortgageCalculationMethod;
  start_date: string;
  installments: number;
  principal: number;
  interest_rate: number;
}

export type MortgageUpdate = Partial<MortgageCreate>;

export interface AmortizationCreate {
  date: string;
  amount: number;
  reduce_term: boolean;
}

export interface PropertyValuation {
  id: string;
  property_id: string;
  date: string;
  value: number;
  created_at: string;
}

export interface RealEstateProperty {
  id: string;
  user_id: string;
  name: string;
  currency: string;
  value: number;
  property_type: PropertyType;
  ownership_percentage: number;
  acquisition_date: string | null;
  address_street: string | null;
  address_zip_code: string | null;
  address_city: string | null;
  address_province: string | null;
  address_country: string | null;
  usufructuary_age: number | null;
  latitude: number | null;
  longitude: number | null;
  valuations: PropertyValuation[];
  created_at: string;
  updated_at: string;
  // Computed
  effective_value: number;
  bare_ownership_multiplier: number | null;
  display_value: number;
  display_value_base_currency: number;
  base_currency: string;
  mortgage: Mortgage | null;
}

export interface RealEstatePropertyCreate {
  name: string;
  currency: string;
  value: number;
  property_type: PropertyType;
  ownership_percentage: number;
  acquisition_date?: string | null;
  address_street?: string | null;
  address_zip_code?: string | null;
  address_city?: string | null;
  address_province?: string | null;
  address_country?: string | null;
  usufructuary_age?: number | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface RealEstatePropertyUpdate {
  name?: string;
  currency?: string;
  value?: number;
  property_type?: PropertyType;
  ownership_percentage?: number;
  acquisition_date?: string | null;
  address_street?: string | null;
  address_zip_code?: string | null;
  address_city?: string | null;
  address_province?: string | null;
  address_country?: string | null;
  usufructuary_age?: number | null;
  latitude?: number | null;
  longitude?: number | null;
}

export interface PropertyValuationCreate {
  date: string;
  value: number;
}

export const propertiesApi = {
  listProperties: async (): Promise<RealEstateProperty[]> => {
    const { data } = await api.get<RealEstateProperty[]>("/api/properties");
    return data;
  },

  getProperty: async (id: string): Promise<RealEstateProperty> => {
    const { data } = await api.get<RealEstateProperty>(`/api/properties/${id}`);
    return data;
  },

  createProperty: async (body: RealEstatePropertyCreate): Promise<RealEstateProperty> => {
    const { data } = await api.post<RealEstateProperty>("/api/properties", body);
    return data;
  },

  updateProperty: async (
    id: string,
    body: RealEstatePropertyUpdate
  ): Promise<RealEstateProperty> => {
    const { data } = await api.put<RealEstateProperty>(`/api/properties/${id}`, body);
    return data;
  },

  deleteProperty: async (id: string): Promise<void> => {
    await api.delete(`/api/properties/${id}`);
  },

  addValuation: async (
    propertyId: string,
    body: PropertyValuationCreate
  ): Promise<PropertyValuation> => {
    const { data } = await api.post<PropertyValuation>(
      `/api/properties/${propertyId}/valuations`,
      body
    );
    return data;
  },

  deleteValuation: async (propertyId: string, valuationId: string): Promise<void> => {
    await api.delete(`/api/properties/${propertyId}/valuations/${valuationId}`);
  },

  createMortgage: async (propertyId: string, body: MortgageCreate): Promise<Mortgage> => {
    const { data } = await api.post<Mortgage>(`/api/properties/${propertyId}/mortgage`, body);
    return data;
  },

  updateMortgage: async (propertyId: string, body: MortgageUpdate): Promise<Mortgage> => {
    const { data } = await api.put<Mortgage>(`/api/properties/${propertyId}/mortgage`, body);
    return data;
  },

  deleteMortgage: async (propertyId: string): Promise<void> => {
    await api.delete(`/api/properties/${propertyId}/mortgage`);
  },

  addAmortization: async (
    propertyId: string,
    body: AmortizationCreate
  ): Promise<MortgageAmortization> => {
    const { data } = await api.post<MortgageAmortization>(
      `/api/properties/${propertyId}/mortgage/amortizations`,
      body
    );
    return data;
  },

  deleteAmortization: async (propertyId: string, amortizationId: string): Promise<void> => {
    await api.delete(
      `/api/properties/${propertyId}/mortgage/amortizations/${amortizationId}`
    );
  },
};
